import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useClickAway from "@/core/helpers/useClickAway";
import { RubleIcon } from "@/shared/icons";
import { CrossIcon } from "@/shared/icons/CrossIcon";
import { SoundButton } from "@/shared/ui";
import { EstateState } from "@/domains/estate/slice";

type Props = {
  onClose: () => void;
  onConfirm: () => void;
  info: EstateState["info"];
};

export default function EstateSaleConfirmModal({ onClose, onConfirm, info }: Props) {
  const { t } = useTranslation("estate");
  const [isLoading, setIsLoading] = useState(false);

  const confirmHandler = useCallback(async () => {
    try {
      setIsLoading(true);
      await onConfirm();
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm]);

  const ref = useClickAway(onClose);

  return (
    <div
      className={clsx(
        "fixed top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
    >
      <div
        ref={ref}
        className={clsx(
          "w-[25.25rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
          "relative p-7.5 flex flex-col items-center greenShadow",
        )}
      >
        <SoundButton
          color="gray"
          variant="soft"
          className="absolute top-2.5 right-2.5 w-[1.875rem] h-[1.875rem] p-0"
          onClick={onClose}
        >
          <CrossIcon size={12} />
        </SoundButton>
        <div className="text-18 font-bold text-white">
          {t(`estate.type.${info.type}.name`)} #{info.id}
        </div>
        <div className="text-15 font-medium text-white/70 mt-1">{info.address}</div>
        <div className="h-[1px] w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
          <div className="h-full w-[5rem] bg-[#84FF97]" />
        </div>
        <div className="text-16 font-medium text-white">
          {t(`estate.type.${info.type}.confirmSaleQuestion`)}
        </div>
        <div
          className={clsx(
            "w-full h-[2.75rem] flex items-center gap-2 justify-center mt-3.5",
            "rounded-[0.5rem] border border-dashed border-[#368D44]",
            "bg-gradient-to-b from-[#368D44]/10 to-[#21652C]/10",
          )}
        >
          <RubleIcon size={24} className="text-[#84FF97] " />
          <div className="text-16 text-white font-bold">{formatPrice(info.price)} ₽</div>
        </div>
        <div className="w-full grid grid-cols-2 gap-2.5 mt-2.5">
          <SoundButton
            data-mz-accent="success"
            className="h-[2.625rem]"
            onClick={confirmHandler}
            loading={isLoading}
            disabled={isLoading}
          >
            {t("estate.yes")}
          </SoundButton>
          <SoundButton
            color="gray"
            variant="soft"
            className="h-[2.625rem]"
            onClick={onClose}
            disabled={isLoading}
          >
            {t("estate.cancel")}
          </SoundButton>
        </div>
      </div>
    </div>
  );
}
